
import { mapGetters, mapActions } from 'vuex'
import moment from 'moment'
import numbro from 'numbro'
import CompareSimulationsColumn from '@/components/CompareSimulationsColumn.vue'
import ChannelCurvesChartMulti from '@/components/simulationDetails/ChannelCurvesChartMulti.vue'
import ComparativeMixChart from '@/components/simulationDetails/ComparativeMixChart.vue'
import TextBox from '@/components/TextBox.vue'
import TextBoxMetric from '@/components/TextBoxMetric.vue'
import { prettyNames } from '@/mixins/prettyNames.js'
import { roasUnitLabel, roasSuffix, roasMantissa } from '@/utils/roasUnits.js'
import { hasHiddenKpis, isKpiHidden, firstVisibleOutputName } from '@/utils/hiddenKpis.js'

export default {
  name: 'WhatIfScenario',
  components: {
    CompareSimulationsColumn,
    ChannelCurvesChartMulti,
    ComparativeMixChart,
    TextBox,
    TextBoxMetric
  },
  mixins: [prettyNames],
  data () {
    return {
      currentTab: 0,
      triggering: false,
      currentOutput: null
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('clients', [
      'currentClient',
      'currentClientObject'
    ]),
    ...mapGetters('simulations', [
      'simulations',
      'simulationsLoading'
    ]),
    isAdHoc () {
      return !!(this.currentClientObject && this.currentClientObject.type === 'mmm')
    },
    strategyCheckPlans () {
      return this.simulations.filter(simulation => simulation.strategy_check)
    },
    hasStrategyCheckPlans () {
      return this.strategyCheckPlans.length > 0
    },
    calculatorPlan () {
      return this.parsePlan(this.strategyCheckPlans.find(s => s.strategy_check_kind === 'calculator'))
    },
    optimizerPlan () {
      return this.parsePlan(this.strategyCheckPlans.find(s => s.strategy_check_kind === 'optimizer'))
    },
    bothReady () {
      return this.planReady(this.calculatorPlan) && this.planReady(this.optimizerPlan)
    },
    loadedSimulations () {
      if (!this.bothReady) return []
      return [this.calculatorPlan, this.optimizerPlan]
    },
    colors () {
      return (this.currentClientObject && this.currentClientObject.channelColors) || {}
    },
    // Prefix for the charts' exported image names, mirroring the plan details view
    // ("{model} - {chart}"). Empty until the model is loaded, which is fine: the
    // charts only render once it is.
    clientName () {
      return (this.currentClientObject && this.currentClientObject.name) || ''
    },
    customKpiName () {
      if (this.currentClientObject && this.currentClientObject.customKpiName) {
        return this.currentClientObject.customKpiName
      }
      return this.$t('views.planOptimizer.defaultKpiName')
    },
    referenceModelConfig () {
      const client = this.currentClientObject
      if (client && client.plan_reference_model && client.plan_reference_model.config) {
        return client.plan_reference_model.config
      }
      if (client && client.reference_model && client.reference_model.config) {
        return client.reference_model.config
      }
      return {}
    },
    // The per-output KPI map, mirroring ModelReport/CompareSimulationsColumn: every real output
    // plus a synthetic "Total" when the outputs are combinable (all additive and not flagged
    // otherwise). Null when the plans carry no per-output breakdown (legacy single-output plans).
    outputs () {
      const plan = this.calculatorPlan || this.optimizerPlan
      if (!plan || !plan.results || !plan.results.sales || !('outputs' in plan.results.sales)) {
        return null
      }
      const sales = plan.results.sales
      // KPIs hidden by the model's Display settings are left out, and so is the Total while
      // any is hidden (it adds up the hidden KPIs too).
      const client = this.currentClientObject
      let outputs = {}
      Object.keys(sales.outputs).forEach(name => {
        if (!isKpiHidden(client, name)) {
          outputs[name] = sales.outputs[name]
        }
      })
      const metadata = this.referenceModelConfig.metadata
      const flagCombinable = !metadata || metadata.combinable_outputs !== false
      const configOutputs = this.referenceModelConfig.outputs
      const allAdditive = Object.keys(sales.outputs).every(name => {
        if (!configOutputs || !configOutputs[name]) return true
        return configOutputs[name].is_additive !== false
      })
      if ('total' in sales && flagCombinable && allAdditive && !hasHiddenKpis(client)) {
        outputs['Total'] = { ...sales }
      }
      return outputs
    },
    outputNames () {
      return this.outputs ? Object.keys(this.outputs) : []
    },
    // 'sum' when the selected output is additive, 'average' otherwise. The "Total" view (or no
    // selection) is additive only when every real output is. Mirrors CompareSimulationsColumn.
    salesDecompositionMode () {
      const configOutputs = this.referenceModelConfig.outputs
      const isOutputAdditive = (outputName) => {
        if (!configOutputs || !configOutputs[outputName]) return true
        return configOutputs[outputName].is_additive !== false
      }
      const isTotal = !this.currentOutput || this.currentOutput === 'Total'
      if (!isTotal) {
        return isOutputAdditive(this.currentOutput) ? 'sum' : 'average'
      }
      const plan = this.calculatorPlan || this.optimizerPlan
      const realOutputs = (plan && plan.results && plan.results.sales && plan.results.sales.outputs)
        ? Object.keys(plan.results.sales.outputs)
        : []
      return realOutputs.every(name => isOutputAdditive(name)) ? 'sum' : 'average'
    },
    // True when Total ROAS can't be computed for the selected output, so the section shows "N/A":
    // the plans carry no spend to divide by. Mirrors the null cases of roasOf.
    roasNotApplicable () {
      return !this.spend
    },
    // Composite unit of the ROAS, when the selected output isn't measured in the spend's unit
    // ("units/USD"). It goes in the box label, like the two boxes beside it.
    roasUnit () {
      return roasUnitLabel(this.outputUnitName, this.spendUnitName)
    },
    roasTotalLabel () {
      const label = this.$t('components.ModelReportComponent.roasTotal')
      return this.roasUnit ? `${label} (${this.roasUnit})` : label
    },
    // Caveats about the figure: it carries a composite unit instead of being a return
    // multiple, and for a non-additive output its numerator is the period total while the
    // KPI box beside it shows the period average. Nothing to warn about when it says N/A.
    roasTotalWarnings () {
      if (this.roasNotApplicable) return []
      return [
        this.roasUnit
          ? this.$t('components.ModelReportComponent.roasCrossUnitWarning', { kpi: this.customKpiName, unit: this.roasUnit })
          : null,
        this.salesDecompositionMode === 'average'
          ? this.$t('views.WhatIfScenario.roasNonAdditiveWarning', { kpi: this.customKpiName })
          : null
      ]
    },
    // The info copy for the Incremental Sales Comparison chart. For non-additive outputs the chart
    // is not accumulated, so it gets a per-period (non-cumulative) explanation.
    incrementalSalesComparisonInfo () {
      const key = this.salesDecompositionMode === 'average'
        ? 'views.WhatIfScenario.incrementalSalesComparisonInfoNonAdditive'
        : 'views.WhatIfScenario.incrementalSalesComparisonInfo'
      return this.$t(key, { kpi: this.customKpiName })
    },
    spendUnitName () {
      const client = this.currentClientObject
      if (client && client.plan_reference_model && client.plan_reference_model.config && 'invest_unit_name' in client.plan_reference_model.config) {
        return client.plan_reference_model.config.invest_unit_name
      } else if (client && client.reference_model && client.reference_model.config && 'invest_unit_name' in client.reference_model.config) {
        return client.reference_model.config.invest_unit_name
      }
      return this.$t('views.planOptimizer.defaultCurrency')
    },
    outputUnitName () {
      const config = this.referenceModelConfig
      if (this.currentOutput && this.currentOutput !== 'Total' &&
          config.outputs && config.outputs[this.currentOutput] && config.outputs[this.currentOutput].sales_unit_name) {
        return config.outputs[this.currentOutput].sales_unit_name
      }
      return config.sales_unit_name || 'USD'
    },
    // The plan period, formatted like the per-plan columns ("Jan 1, 2025 → Dec 31, 2025"). Both
    // compared plans share the same start/end dates, so either plan's dates serve. Empty when no
    // plan with dates is available.
    dateRangeFmt () {
      const plan = this.calculatorPlan || this.optimizerPlan
      if (!plan || !plan.date_from || !plan.date_to) return ''
      return `${moment(plan.date_from).format('ll')} → ${moment(plan.date_to).format('ll')}`
    },
    spend () {
      // The optimizer reallocates the same total budget, so both plans share one spend value.
      const plan = this.calculatorPlan
      if (plan && plan.results && plan.results.invest && typeof plan.results.invest.budget === 'number') {
        return plan.results.invest.budget
      }
      return 0
    },
    salesActualFmt () {
      return this.formatSales(this.salesOf(this.calculatorPlan))
    },
    salesOptimizedFmt () {
      return this.formatSales(this.salesOf(this.optimizerPlan))
    },
    salesLiftFmt () {
      return this.formatLift(this.salesOf(this.calculatorPlan), this.salesOf(this.optimizerPlan))
    },
    roasActualFmt () {
      return this.formatRoas(this.roasOf(this.calculatorPlan))
    },
    roasOptimizedFmt () {
      return this.formatRoas(this.roasOf(this.optimizerPlan))
    },
    roasLiftFmt () {
      const act = this.roasOf(this.calculatorPlan)
      const opt = this.roasOf(this.optimizerPlan)
      if (act === null || opt === null) return ''
      return this.formatLift(act, opt)
    },
    // Two-series data for the Incremental Sales Comparison line chart, in the
    // { seriesName: { x, y } } shape ChannelCurvesChartMulti expects. Optimized first.
    incrementalSalesData () {
      const optimized = this.incrementalSalesSeries(this.optimizerPlan)
      const actual = this.incrementalSalesSeries(this.calculatorPlan)
      if (!optimized || !actual) return null
      return { Optimized: optimized, Actual: actual }
    },
    incrementalSalesColors () {
      return { Optimized: '#4B67B8', Actual: '#F5A623' }
    },
    incrementalSalesNameTranslations () {
      return {
        Optimized: this.$t('views.WhatIfScenario.planOptimizer'),
        Actual: this.$t('views.WhatIfScenario.planCalculator')
      }
    },
    // Draw the Optimized Plan line dashed to distinguish it from the (actual) baseline.
    incrementalSalesLineStyles () {
      return JSON.stringify({ Optimized: { type: 'dashed' } })
    },
    prettyMediaNamesDict () {
      const client = this.currentClientObject
      return (client && client.pretty_names && client.pretty_names.media) || {}
    },
    prettyColors () {
      return this.prettyTranslateNames(this.colors, [''], this.prettyMediaNamesDict)
    },
    comparativeMixActual () {
      return this.comparativeMixOf(this.calculatorPlan)
    },
    comparativeMixOptimized () {
      return this.comparativeMixOf(this.optimizerPlan)
    },
    availableTabs () {
      if (this.loadedSimulations.length === 0) return []
      const candidates = [
        { key: 'fullPeriodSummary', check: () => true },
        { key: 'monthlyDetailView', check: sim => this.hasMonthlyDetail(sim) },
        { key: 'weeklyDetailView', check: sim => this.hasWeeklyDetail(sim) },
        { key: 'touchpoints', check: sim => this.hasTouchpoints(sim) },
        { key: 'segments', check: sim => this.hasSegments(sim) }
      ]
      return candidates
        .filter(c => this.loadedSimulations.every(sim => c.check(sim)))
        .map(c => ({ key: c.key }))
    },
    activeSection () {
      if (this.availableTabs.length === 0) return 'fullPeriodSummary'
      const tab = this.availableTabs[this.currentTab] || this.availableTabs[0]
      return tab.key
    }
  },
  watch: {
    // When arrived at via a notification (which carries the model id), select that model so the
    // view shows the right scenario rather than whatever was globally selected.
    '$route.params': {
      immediate: true,
      handler () {
        if (this.$route.params.client) {
          this.setCurrentClient(this.$route.params.client)
        }
      }
    },
    currentClient: {
      immediate: true,
      handler () {
        if (this.currentClient) {
          this.loadSimulations({ client: this.currentClient })
        }
      }
    },
    'availableTabs.length' (newLen) {
      if (this.currentTab >= newLen) {
        this.currentTab = 0
      }
    },
    // Default the selected KPI once outputs are known: prefer the combined "Total" view, falling
    // back to the first output. Reset when the selection is no longer valid.
    outputNames: {
      handler (newVal) {
        if (!newVal || newVal.length === 0) {
          this.currentOutput = null
          return
        }
        if (!this.currentOutput || !newVal.includes(this.currentOutput)) {
          this.currentOutput = newVal.includes('Total') ? 'Total' : newVal[0]
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('clients', [
      'setCurrentClient'
    ]),
    ...mapActions('simulations', [
      'loadSimulations',
      'triggerStrategyCheckPlans'
    ]),
    // Trigger on-demand creation of the strategy-check plans. Once the backend adds them, the
    // simulations listener flips hasStrategyCheckPlans → the "computing" state takes over, and
    // bothReady eventually renders the full section. A failed-precondition rejection (the plans
    // already exist / are being generated) just clears the local spinner — the listener shows
    // the right state — so it isn't surfaced as a hard error.
    async triggerPlans () {
      if (this.triggering) return
      this.triggering = true
      try {
        await this.triggerStrategyCheckPlans({
          client: this.currentClient,
          userId: this.authUser && this.authUser.uid
        })
      } catch (error) {
        this.triggering = false
        console.error('Failed to trigger What-If Scenario plans:', error)
      }
    },
    parsePlan (plan) {
      if (!plan) return null
      // Support stringified results (Firebase chokes on very large results objects)
      if (plan.results && typeof plan.results === 'string') {
        try {
          return { ...plan, results: JSON.parse(plan.results) }
        } catch (e) {
          console.error('Failed to parse strategy check plan results:', e)
          return null
        }
      }
      return plan
    },
    planReady (plan) {
      return !!(plan && plan.results)
    },
    // The { mix, budget } input for the Comparative Mix chart, with channel keys
    // remapped to pretty names so they match prettyColors. Null when the plan
    // carries no invest mix, which hides the section.
    comparativeMixOf (plan) {
      if (!plan || !plan.results || !plan.results.invest || !plan.results.invest.mix) return null
      const invest = plan.results.invest
      return {
        mix: this.prettyTranslateNames(invest.mix, [''], this.prettyMediaNamesDict),
        budget: typeof invest.budget === 'number' ? invest.budget : 0
      }
    },
    // The sales output object for a plan, honoring the selected KPI: the chosen output when one is
    // selected (and it isn't the synthetic "Total"), otherwise results.sales when it carries a
    // total, otherwise the first entry of results.sales.outputs.
    salesOutputObject (plan) {
      if (!plan || !plan.results || !plan.results.sales) return null
      const sales = plan.results.sales
      if (this.currentOutput && this.currentOutput !== 'Total' && sales.outputs && this.currentOutput in sales.outputs) {
        return sales.outputs[this.currentOutput]
      }
      if ('total' in sales && !(sales.outputs && hasHiddenKpis(this.currentClientObject))) return sales
      if (sales.outputs) {
        const firstKey = firstVisibleOutputName(this.currentClientObject, sales.outputs)
        return firstKey ? sales.outputs[firstKey] : null
      }
      return null
    },
    // Total sales for a plan, aggregated with the selected output's mode: a sum for additive
    // outputs, an average for non-additive ones (so a non-additive KPI shows a meaningful level
    // rather than a meaningless sum). Falls back to the precomputed total when no by-period
    // decomposition is available.
    salesOf (plan) {
      const obj = this.salesOutputObject(plan)
      if (!obj) return null
      if (obj.sales_decomposition_by_period) {
        return this.aggregateSalesDecompositionByPeriod(obj.sales_decomposition_by_period, this.salesDecompositionMode).total
      }
      return typeof obj.total === 'number' ? obj.total : null
    },
    // Total ROAS for a plan: (Instant Marketing + Carry-over Marketing) / Spend, from the same
    // components the Sales Decomposition chart shows. The numerator is always the period total,
    // never the per-period average: the denominator is the whole period's spend, so for a
    // non-additive output this figure deliberately doesn't match the averaged KPI box beside it.
    roasOf (plan) {
      if (!this.spend) return null
      const obj = this.salesOutputObject(plan)
      if (!obj || !obj.sales_decomposition_by_period) return null
      const aggregated = this.aggregateSalesDecompositionByPeriod(
        obj.sales_decomposition_by_period,
        'sum'
      )

      let marketingValue = 0
      let found = false
      aggregated.components.forEach(c => {
        if (this.isMarketingComponent(c.name)) {
          marketingValue += c.value
          found = true
        }
      })
      if (!found) return null
      return marketingValue / this.spend
    },
    // True for the Instant Marketing or Carry-over Marketing decomposition components,
    // matching the raw keys ('marketing'/'marketing_residual') as well as the English and
    // localized display labels. Shared by roasOf and incrementalSalesSeries.
    isMarketingComponent (name) {
      const lower = String(name).toLowerCase()
      const localizedInstant = String(this.$t('components.simulationDetails.SalesDecomposition.instantMarketing')).toLowerCase()
      const localizedCarryOver = String(this.$t('components.simulationDetails.SalesDecomposition.carryOverMarketing')).toLowerCase()
      const isInstant = name === 'marketing' || name === 'Marketing' ||
        lower.includes('instant marketing') || lower === localizedInstant
      const isCarryOver = name === 'marketing_residual' ||
        lower.includes('carry-over marketing') ||
        lower.includes('carry over marketing') ||
        lower.includes('carryover marketing') ||
        lower === localizedCarryOver
      return isInstant || isCarryOver
    },
    // Incremental sales (marketing contribution) for a plan, as { x, y }: the element-wise sum of
    // the Instant + Carry-over Marketing components of sales_decomposition_by_period, accumulated
    // period by period. For additive outputs each point is the running total up to that period; for
    // non-additive outputs (which can't be summed meaningfully) each point is instead the running
    // average of all periods up to it. Returns null when no decomposition is available.
    incrementalSalesSeries (plan) {
      const obj = this.salesOutputObject(plan)
      if (!obj || !obj.sales_decomposition_by_period) return null
      const byPeriod = obj.sales_decomposition_by_period
      let x = null
      let total = null
      Object.keys(byPeriod).forEach(name => {
        if (!this.isMarketingComponent(name)) return
        const component = byPeriod[name]
        const ys = (component && Array.isArray(component.y)) ? component.y : []
        if (!total) {
          total = ys.map(v => (typeof v === 'number' ? v : 0))
        } else {
          ys.forEach((v, i) => {
            total[i] = (total[i] || 0) + (typeof v === 'number' ? v : 0)
          })
        }
        if (!x && component && Array.isArray(component.x) && component.x.length > 0) {
          x = component.x
        }
      })
      if (!total || !x || x.length === 0) return null
      if (this.salesDecompositionMode === 'average') {
        // Non-additive output: accumulate as a running average (each point is the mean of all
        // periods up to it) instead of a running sum, so the cumulative line stays meaningful.
        let runningSum = 0
        const cumulativeAvg = total.map((v, i) => {
          runningSum += v
          return runningSum / (i + 1)
        })
        return { x: x, y: cumulativeAvg }
      }
      let running = 0
      const cumulative = total.map(v => {
        running += v
        return running
      })
      return { x: x, y: cumulative }
    },
    // Aggregates a sales_decomposition_by_period object into per-component totals — a copy of
    // CompareSimulationsColumn.aggregateSalesDecompositionByPeriod so the ROAS uses identical numbers.
    aggregateSalesDecompositionByPeriod (byPeriod, mode = 'sum') {
      if (!byPeriod || typeof byPeriod !== 'object') {
        return { components: [], total: 0 }
      }
      const components = Object.keys(byPeriod).map(name => {
        const ys = (byPeriod[name] && Array.isArray(byPeriod[name].y)) ? byPeriod[name].y : []
        const sum = ys.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0)
        const value = mode === 'average' && ys.length > 0 ? sum / ys.length : sum
        return { name, value }
      })
      const total = components.reduce((a, c) => a + c.value, 0)
      return { components, total }
    },
    formatSales (value) {
      if (value === null || typeof value !== 'number' || isNaN(value)) return '-'
      return numbro(value).format({ average: true, lowPrecision: false, mantissa: 1, optionalMantissa: true })
    },
    formatRoas (value) {
      if (value === null) return this.$t('components.ModelReportComponent.notApplicable')
      return numbro(value).format({
        thousandSeparated: true,
        mantissa: roasMantissa(value),
        optionalMantissa: true,
        trimMantissa: true
      }) + roasSuffix(this.outputUnitName, this.spendUnitName)
    },
    formatLift (actual, optimized) {
      if (actual === null || optimized === null || typeof actual !== 'number' || actual === 0) return ''
      const pct = (optimized - actual) / actual * 100
      return numbro(Math.abs(pct)).format({ thousandSeparated: true, mantissa: 1 }) + '%'
    },
    hasMonthlyDetail (sim) {
      const r = sim.results
      return !!(r && r.invest && r.invest.media_spend && r.invest.time && Object.keys(r.invest.time).length > 1)
    },
    hasWeeklyDetail (sim) {
      const r = sim.results
      return !!(r && r.weekly_detail && r.weekly_detail.sales && r.weekly_detail.sales.time && Object.keys(r.weekly_detail.sales.time).length > 1)
    },
    hasTouchpoints (sim) {
      const r = sim.results
      if (!r || !r.invest || !r.invest.mix) return false
      const firstKey = Object.keys(r.invest.mix)[0]
      return !!(firstKey && r.invest.mix[firstKey] && r.invest.mix[firstKey].touchpoints)
    },
    hasSegments (sim) {
      const r = sim.results
      if (!r) return false
      if (r.invest && r.invest.segments) return true
      if (!r.sales) return false
      const outputs = r.sales.outputs ? Object.values(r.sales.outputs) : [r.sales]
      for (const out of outputs) {
        if (!out) continue
        if (out.microaudience) return true
        if (out.marketing) {
          const firstChannel = Object.keys(out.marketing)[0]
          if (firstChannel && out.marketing[firstChannel] && out.marketing[firstChannel].segments) return true
        }
      }
      return false
    }
  }
}
